/**
 * Service Solicitações Internas (ASM/AST/ASP/RQA/RQI) — módulo Suprimentos.
 *
 * Duas superfícies: colaborador (cria e acompanha as próprias) e gestão
 * (autoriza/recusa/atende + lotes de peças MDF com export .xls do
 * otimizador). Cada função recebe `request` (do useAuth) como 1º argumento.
 */
import { ACCESS_API_URL } from "../config/api";

const ASSISTENCIAS = "/api/solicitacoes/assistencias";
const REQUISICOES = "/api/solicitacoes/requisicoes";
const LOTES = "/api/solicitacoes/lotes";


function montarQuery(params = {}) {
  const usp = new URLSearchParams();
  ["situacao", "tipo", "num_pedido", "busca", "incluir_juntadas"].forEach((chave) => {
    if (params[chave]) usp.set(chave, params[chave]);
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
}


// ---------- Assistências (ASM/AST/ASP) ----------

export function listarAssistencias(request, params) {
  return request(`${ASSISTENCIAS}${montarQuery(params)}`);
}

export function obterAssistencia(request, id) {
  return request(`${ASSISTENCIAS}/${id}`);
}

export function criarAssistencia(request, dados) {
  return request(ASSISTENCIAS, {
    method: "POST", body: JSON.stringify(dados),
  });
}

// ---------- Requisições (RQA/RQI) ----------

export function listarRequisicoes(request, params) {
  return request(`${REQUISICOES}${montarQuery(params)}`);
}

export function obterRequisicao(request, id) {
  return request(`${REQUISICOES}/${id}`);
}

export function criarRequisicao(request, dados) {
  return request(REQUISICOES, {
    method: "POST", body: JSON.stringify(dados),
  });
}

// ---------- Transições (valem para os 2 agregados) ----------

function base(familia) {
  return familia === "requisicao" ? REQUISICOES : ASSISTENCIAS;
}

// Autoriza e, opcionalmente, classifica os itens no mesmo passo (Transition
// Form): `classificacoes` = [{item_id, demanda, nao_conformidade,
// processo_origem, causa_raiz, imputacao, responsavel_nc_nome}]. Sem
// item_id a linha vale para todos os itens. silentError: o modal mostra o
// `detalhe` (ex.: classificação obrigatória faltando) ao lado do campo.
export function autorizarSolicitacao(request, familia, id, { classificacoes } = {}) {
  return request(`${base(familia)}/${id}/autorizar`, {
    method: "POST",
    body: JSON.stringify(classificacoes?.length ? { classificacoes } : {}),
    silentError: true,
  });
}

// Reclassifica itens em qualquer situação (auditado).
export function classificarSolicitacao(request, familia, id, classificacoes) {
  return request(`${base(familia)}/${id}/classificar`, {
    method: "POST", body: JSON.stringify({ classificacoes }), silentError: true,
  });
}

// ---------- Marcos manuais e retirada assinada ----------

// Marca "pronta para retirada" (AUTORIZADA/EM_ANDAMENTO; idempotente).
export function marcarProntaSolicitacao(request, familia, id) {
  return request(`${base(familia)}/${id}/marcar-pronta`, { method: "POST" });
}

/** Retiradas já registradas (sem assinatura) + saldo por item
 * ({item_id, descricao, quantidade, retirado, falta}). */
export function listarRetiradas(request, familia, id) {
  return request(`${base(familia)}/${id}/retiradas`);
}

/** Uma retirada com assinatura e foto — para o comprovante. */
export function obterRetirada(request, familia, id, retiradaId) {
  return request(`${base(familia)}/${id}/retiradas/${retiradaId}`);
}

/** Registra quem retirou na expedição: nome, documento, assinatura
 * (data URL PNG), itens/quantidades (parcial permitido), foto opcional.
 * Resposta: detalhe da solicitação + `retirada`. silentError: o modal
 * mostra o `detalhe` inline. */
export function registrarRetirada(request, familia, id, dados) {
  return request(`${base(familia)}/${id}/registrar-retirada`, {
    method: "POST", body: JSON.stringify(dados), silentError: true,
  });
}

// ---------- Prazo por rota ----------

export function replanejarPrazo(request, familia, id, { nova_data, motivo, texto }) {
  return request(`${base(familia)}/${id}/replanejar-prazo`, {
    method: "POST",
    body: JSON.stringify({ nova_data, motivo, texto: texto || "" }),
    silentError: true,
  });
}

export function ajustarRota(request, familia, id, { rota, motivo }) {
  return request(`${base(familia)}/${id}/ajustar-rota`, {
    method: "POST", body: JSON.stringify({ rota, motivo }), silentError: true,
  });
}

// ---------- Catálogos de classificação + configuração ----------

// `tipo` nas funções abaixo: "processos" | "ncs" | "causas" | "demandas".
const CATALOGOS = "/api/solicitacoes/catalogos";

/** Tudo que o modal "Autorizar e classificar" precisa numa request:
 * catálogos ativos + listas fixas (imputações, famílias, níveis, 6M). */
export function obterCatalogosSelect(request) {
  return request("/api/solicitacoes/catalogos-select");
}

/** Regra de imputação sugerida (pura, no backend) para a combinação. */
export function obterImputacaoSugerida(request, { demanda, processo, causa, nc } = {}) {
  const usp = new URLSearchParams();
  if (demanda) usp.set("demanda", demanda);
  if (processo) usp.set("processo", processo);
  if (causa) usp.set("causa", causa);
  if (nc) usp.set("nc", nc);
  return request(`/api/solicitacoes/imputacao-sugerida?${usp.toString()}`, {
    silentError: true,
  });
}

export function listarCatalogo(request, tipo, { inativos = false } = {}) {
  return request(`${CATALOGOS}/${tipo}${inativos ? "?inativos=1" : ""}`);
}

export function criarItemCatalogo(request, tipo, dados) {
  return request(`${CATALOGOS}/${tipo}`, {
    method: "POST", body: JSON.stringify(dados), silentError: true,
  });
}

export function atualizarItemCatalogo(request, tipo, id, dados) {
  return request(`${CATALOGOS}/${tipo}/${id}`, {
    method: "PATCH", body: JSON.stringify(dados), silentError: true,
  });
}

// 409 quando o item está em uso ou é de sistema — a tela orienta a desativar.
export function excluirItemCatalogo(request, tipo, id) {
  return request(`${CATALOGOS}/${tipo}/${id}`, { method: "DELETE", silentError: true });
}

/** Configuração de Solicitações da unidade (+ `padrao` com os defaults). */
export function obterConfiguracaoSolicitacoes(request) {
  return request("/api/solicitacoes/configuracao", { silentError: true });
}

export function salvarConfiguracaoSolicitacoes(request, dados) {
  return request("/api/solicitacoes/configuracao", {
    method: "PUT", body: JSON.stringify(dados), silentError: true,
  });
}

/** Regras de prazo por rota ({regras, rotas, padrao}). */
export function obterRegrasPrazo(request) {
  return request("/api/solicitacoes/regras-prazo", { silentError: true });
}

export function salvarRegrasPrazo(request, regras) {
  return request("/api/solicitacoes/regras-prazo", {
    method: "PUT", body: JSON.stringify({ regras }), silentError: true,
  });
}

export function recusarSolicitacao(request, familia, id, motivo) {
  return request(`${base(familia)}/${id}/recusar`, {
    method: "POST", body: JSON.stringify({ motivo }),
  });
}

export function iniciarSolicitacao(request, familia, id) {
  return request(`${base(familia)}/${id}/iniciar`, { method: "POST" });
}

export function atenderSolicitacao(request, familia, id) {
  return request(`${base(familia)}/${id}/atender`, { method: "POST" });
}

// Reabre uma solicitação finalizada (Atendida/Recusada/Cancelada) para uma
// situação ativa (ABERTA/AUTORIZADA/EM_ANDAMENTO) — arrastar de volta no kanban.
// ------------------------------------------------ junção (23/09/2026)

/**
 * O que juntar faria, sem gravar: { principal, absorvidas[{numero, linhas,
 * solicitante}], linhas_antes, linhas_movidas, prazo_antes, prazo_depois,
 * prazo_vem_de, perde_pronta, perde_separada }. Regra recusada = 400 com
 * `detalhe` (o modal mostra; sem toast automático).
 */
export function previaJuncao(request, familia, principalId, incorporar) {
  return request(`${base(familia)}/${principalId}/juntar/previa`, {
    method: "POST", body: JSON.stringify({ incorporar }), silentError: true,
  });
}

/** Junta `incorporar` na principal; devolve o Detalhe da principal. */
export function juntarSolicitacoes(request, familia, principalId, incorporar) {
  return request(`${base(familia)}/${principalId}/juntar`, {
    method: "POST", body: JSON.stringify({ incorporar }), silentError: true,
  });
}

/** Reabrir exige motivo (≥ 5 letras) — vai para a auditoria e a linha do tempo. */
export function reabrirSolicitacao(request, familia, id, situacao, motivo) {
  return request(`${base(familia)}/${id}/reabrir`, {
    method: "POST", body: JSON.stringify({ situacao, motivo }),
  });
}

// Exclui a solicitação DEFINITIVAMENTE (hard delete). Só quem tem
// solicitacao:delete. silentError: a tela trata o erro (ex.: peças em lote).
export function excluirSolicitacao(request, familia, id) {
  return request(`${base(familia)}/${id}`, {
    method: "DELETE", silentError: true,
  });
}

/**
 * Cancelar leva junto o ambiente de produção da assistência. Com trabalho já
 * feito na fábrica o backend recusa com `payload.precisa_confirmar_perda` e as
 * etapas (ver AvisoPerdaProducao); reenvie com `confirmarPerda: true`.
 */
export function cancelarSolicitacao(request, familia, id, motivo = "", { confirmarPerda = false } = {}) {
  return request(`${base(familia)}/${id}/cancelar`, {
    method: "POST", body: JSON.stringify({ motivo, confirmar_perda: confirmarPerda }),
  });
}

// ------------------------------------------------ conversa e novidade (B6)

/** { comentarios: [...] } — abrir já marca como lidas as menções a você. */
export function listarComentariosSolicitacao(request, familia, id) {
  return request(`${base(familia)}/${id}/comentarios`, { silentError: true });
}

export function comentarSolicitacao(request, familia, id, texto, mencionados = []) {
  return request(`${base(familia)}/${id}/comentarios`, {
    method: "POST", body: JSON.stringify({ texto, mencionados }),
  });
}

export function pessoasMencionaveis(request, familia, id) {
  return request(`${base(familia)}/${id}/pessoas-mencionaveis`, { silentError: true });
}

/** Solta reservas de estoque presas a esta requisição (encerrada ou item já saído). */
export function soltarReservasPresas(request, familia, id) {
  return request(`${base(familia)}/${id}/soltar-reservas-presas`, { method: "POST" });
}

/** Quem pediu abriu a solicitação: some o aviso do sino e o ponto no Hub. */
export function marcarNovidadeVista(request, familia, id) {
  return request(`${base(familia)}/${id}/novidade-vista`, { method: "POST", silentError: true });
}

/** { aguardando_autorizacao, pecas_sem_lote, sem_separacao, reservas_presas,
 *   causa_pendente, mencoes, novidades, total, rotulos } — sino e widgets. */
export function obterPendenciasSolicitacoes(request) {
  return request("/api/solicitacoes/pendencias-resumo", { silentError: true });
}

// Move o card para outra coluna operacional do quadro (sem mudar o status).
export function moverEtapaSolicitacao(request, familia, id, etapa) {
  return request(`${base(familia)}/${id}/mover-etapa`, {
    method: "POST", body: JSON.stringify({ etapa }),
  });
}

// Marca a requisição (RQA/RQI) como separada no almoxarifado (grava quem +
// quando) — para o comprovante de separação. Só requisições.
export function separarSolicitacao(request, id) {
  return request(`${REQUISICOES}/${id}/separar`, { method: "POST" });
}

// Plano de Suprimentos (D35): separa UM item — com produto, dá baixa no
// estoque (depósito de saída configurado ou o informado) e libera a reserva.
export function separarItemSolicitacao(request, id, itemId, { quantidade, deposito } = {}) {
  return request(`${REQUISICOES}/${id}/itens/${itemId}/separar`, {
    method: "POST",
    body: JSON.stringify({ quantidade: quantidade || null, deposito: deposito || null }),
  });
}

// ---------- Fotos: girar / cortar / restaurar ----------
// As rotas são ANINHADAS na solicitação de propósito: os modelos de foto não
// têm escopo de tenant próprio, então a foto só é alcançável através do
// queryset já filtrado da solicitação. Nunca criar um /fotos/{id} solto.
// silentError: o visualizador mostra o erro na própria tela.

export function editarFotoSolicitacao(request, familia, id, fotoId, imagem) {
  return request(`${base(familia)}/${id}/fotos/${fotoId}/imagem`, {
    method: "POST", body: JSON.stringify({ imagem }), silentError: true,
  });
}

export function restaurarFotoSolicitacao(request, familia, id, fotoId) {
  return request(`${base(familia)}/${id}/fotos/${fotoId}/restaurar`, {
    method: "POST", silentError: true,
  });
}

// ---------- Selects leves do formulário (gate solicitacao:create) ----------
// Menor privilégio: o colaborador escolhe cliente/material sem precisar de
// comercial:view nem estoque:view.

export function listarClientesSelect(request) {
  return request("/api/solicitacoes/clientes-select");
}

export function listarMateriaisSelect(request) {
  return request("/api/solicitacoes/materiais-select");
}

export function listarFitasSelect(request) {
  return request("/api/solicitacoes/fitas-select");
}

// ---------- Lotes de peças MDF (gestão) ----------

export function listarLotes(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.busca) usp.set("busca", params.busca);
  const qs = usp.toString();
  return request(`${LOTES}${qs ? `?${qs}` : ""}`);
}

export function obterLote(request, id) {
  return request(`${LOTES}/${id}`);
}

export function listarPecasElegiveis(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.num_pedido) usp.set("num_pedido", params.num_pedido);
  const qs = usp.toString();
  return request(`${LOTES}/pecas-elegiveis${qs ? `?${qs}` : ""}`);
}

export function gerarLote(request, { numero, nome, pecaIds }) {
  return request(LOTES, {
    method: "POST",
    body: JSON.stringify({ numero, nome, peca_ids: pecaIds }),
  });
}

/** Desfaz um lote gerado: as peças voltam a ficar elegíveis para novo lote
 * e o número fica livre. Devolve `{ pecas_liberadas }`.
 * silentError: o AsyncButton do LotesTab já toasta a falha (evita toast duplo). */
export function desfazerLote(request, id) {
  return request(`${LOTES}/${id}/desfazer`, {
    method: "POST", silentError: true,
  });
}

// ---------- Config de exportação (mapeamento coluna→campo do XLS) ----------

/** Mapeamento efetivo + campos disponíveis + colunas fixas do XLS de lote.
 * silentError: o painel trata o erro inline (evita toast duplicado). */
export function obterConfigExportacao(request) {
  return request(`${LOTES}/config-exportacao`, { silentError: true });
}

/** Sobrescreve o mapeamento coluna→campo do XLS (lista de 26 {coluna, campo_key}). */
export function salvarConfigExportacao(request, mapeamento) {
  return request(`${LOTES}/config-exportacao`, {
    method: "PUT",
    body: JSON.stringify({ mapeamento }),
  });
}

// ---------- Painel de Assistências (Gestão — analytics) ----------

const PAINEL = "/api/solicitacoes/painel-assistencias";

/**
 * Query string do Painel v2. Valores em lista viram parâmetro repetido;
 * vazios/nulos caem fora. Só chaves da allowlist do backend chegam aqui —
 * parâmetro desconhecido é 400, então nada é adicionado "por via das dúvidas".
 */
export function montarQueryPainel(recorte = {}) {
  const usp = new URLSearchParams();
  Object.entries(recorte).forEach(([chave, valor]) => {
    if (valor === null || valor === undefined || valor === "") return;
    if (Array.isArray(valor)) {
      valor.filter((v) => v !== null && v !== undefined && v !== "")
        .forEach((v) => usp.append(chave, String(v)));
      return;
    }
    usp.set(chave, String(valor));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
}

/** Painel v2: recorte (`periodo` ou `de`/`ate`, `data_base`, `tipo`,
 * `situacao` e filtros dimensionais) → blocos volume/backlog/prazos/
 * qualidade/custos/reincidencia/pessoas/lotes/dados. */
export function obterPainelAssistencias(request, recorte = {}) {
  return request(`${PAINEL}${montarQueryPainel(recorte)}`, { silentError: true });
}

/** Drill paginado de um conjunto do recorte (recorte, abertas,
 * aguardando_autorizacao, vencidas, sem_classificacao, prontas_sem_retirada,
 * pecas_sem_lote, sem_prazo). */
export function listarItensPainel(request, recorte, { conjunto = "recorte", page = 1, page_size = 50 } = {}) {
  return request(
    `${PAINEL}/itens${montarQueryPainel({ ...recorte, conjunto, page, page_size })}`,
    { silentError: true },
  );
}

/** Facetas de uma dimensão sob os demais filtros ({valor, label, total}). */
export function listarOpcoesPainel(request, recorte, dimensao, busca = "") {
  return request(
    `${PAINEL}/opcoes${montarQueryPainel({ ...recorte, dimensao, busca })}`,
    { silentError: true },
  );
}

// ---------- Config de colunas do kanban (Gestão) ----------

const CONFIG_COLUNAS_KANBAN = "/api/solicitacoes/config-colunas-kanban";

function _qsTipo(tipo) {
  // "" ou "TODOS" => config global (sem ?tipo).
  return tipo && tipo !== "TODOS" ? `?tipo=${encodeURIComponent(tipo)}` : "";
}

/** Colunas efetivas do kanban (por tipo) + padrão + situações disponíveis.
 * tipo "" / "TODOS" = config global. silentError: carga de background com
 * fallback COLUNAS_PADRAO / erro inline no painel. */
export function obterConfigColunas(request, tipo = "") {
  return request(`${CONFIG_COLUNAS_KANBAN}${_qsTipo(tipo)}`, {
    silentError: true,
  });
}

/** Sobrescreve as colunas do kanban do tipo (lista de {id,titulo,cor,situacoes}). */
export function salvarConfigColunas(request, tipo, colunas) {
  return request(`${CONFIG_COLUNAS_KANBAN}${_qsTipo(tipo)}`, {
    method: "PUT",
    body: JSON.stringify({ colunas }),
  });
}

/**
 * "490 - ASM CLIENTE EXEMPLO.xls": número digitado + descrição. O servidor
 * manda o nome pronto (`nome_da_planilha`); a conta daqui é a MESMA regra
 * (`solicitacoes/xls.py::nome_do_lote`), para lote vindo de resposta antiga.
 */
export function nomeDaPlanilhaDoLote(lote) {
  if (lote?.nome_da_planilha) return lote.nome_da_planilha;
  const numero = String(lote?.numero || "").trim();
  const nome = String(lote?.nome || "").trim();
  let base = `${numero} - ${nome}`;
  if (!nome) base = numero;
  else if (!numero || nome === numero || nome.startsWith(`${numero} `) || nome.startsWith(`${numero}-`)) base = nome;
  return `${base.replace(/[\\/:*?"<>|]+/g, "-")}.xls`;
}

/** Reenvia ao PharuCut o lote que não chegou lá. silentError: quem chama
 * (AsyncButton) já mostra a falha. */
export function enviarLoteAoCut(request, id) {
  return request(`${LOTES}/${id}/enviar-ao-cut`, { method: "POST", silentError: true });
}

/**
 * Baixa o .xls BIFF do lote (formato PCP do otimizador de corte).
 * Fetch raw autenticado (blob não passa pelo wrapper `request`).
 */
export async function baixarXlsLote(lote) {
  // NUNCA process.env direto: sem a env var no build, virava "" e o
  // fetch relativo caia no CloudFront, que devolve o index.html da SPA
  // — o "XLS" baixado era a home do site (incidente real, 2026-08-13).
  const apiUrl = ACCESS_API_URL;
  const session = JSON.parse(
    localStorage.getItem("pharuerp:access-session") || "{}",
  );
  const headers = {};
  if (session?.accessToken) {
    headers.Authorization = `Bearer ${session.accessToken}`;
  }
  const resp = await fetch(`${apiUrl}${LOTES}/${lote.id}/xls`, { headers });
  if (!resp.ok) {
    throw new Error(`Falha ao baixar o XLS do lote (${resp.status}).`);
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeDaPlanilhaDoLote(lote);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}


// ---------- Constantes compartilhadas pelas telas ----------

export const TIPOS_SOLICITACAO = [
  {
    value: "ASM", familia: "assistencia", label: "Assistência à Montagem",
    hint: "Refazer ou repor peça durante a montagem no cliente",
  },
  {
    value: "AST", familia: "assistencia", label: "Assistência Técnica",
    hint: "Refazer ou repor peça no pós-entrega",
  },
  {
    value: "ASP", familia: "assistencia", label: "Assistência à Produção",
    hint: "Refazer peça na fábrica (setor e lote)",
  },
  {
    value: "RQA", familia: "requisicao", label: "Requisição de Almoxarifado",
    hint: "Pedir itens do almoxarifado (ferragens, parafusos...)",
  },
  {
    value: "RQI", familia: "requisicao", label: "Requisição de Insumo",
    hint: "Pedir insumos de produção (fitas, colas...)",
  },
];

export const SITUACOES_SOLICITACAO = [
  { value: "ABERTA", label: "Aguardando autorização", tone: "warning" },
  { value: "AUTORIZADA", label: "Autorizada", tone: "info" },
  { value: "EM_ANDAMENTO", label: "Em andamento", tone: "info" },
  { value: "ATENDIDA", label: "Atendida", tone: "success" },
  { value: "RECUSADA", label: "Recusada", tone: "danger" },
  { value: "CANCELADA", label: "Cancelada", tone: "neutral" },
  // Absorvida numa junção: as peças/itens seguem na principal. Não aparece
  // no quadro da Gestão; o Hub de quem pediu mostra com o link (23/09/2026).
  { value: "JUNTADA", label: "Juntada", tone: "neutral" },
];

export const SITUACAO_LABELS = Object.fromEntries(
  SITUACOES_SOLICITACAO.map((s) => [s.value, s.label]),
);
export const SITUACAO_TONES = Object.fromEntries(
  SITUACOES_SOLICITACAO.map((s) => [s.value, s.tone]),
);

export const MOTIVOS_PECA = [
  { value: "ERRO_PROJETO", label: "Erro de projeto" },
  { value: "ERRO_PRODUCAO", label: "Erro de produção" },
  { value: "DANO_TRANSPORTE", label: "Dano no transporte" },
  { value: "DANO_MONTAGEM", label: "Dano na montagem" },
  { value: "PECA_FALTANTE", label: "Peça faltante" },
  { value: "ALTERACAO_CLIENTE", label: "Alteração do cliente" },
  { value: "OUTRO", label: "Outro" },
];

// Motivo da Requisição de Almoxarifado (RQA) — select obrigatório.
export const MOTIVOS_REQUISICAO = [
  { value: "REPOSICAO_ESTOQUE", label: "Reposição de estoque" },
  { value: "MATERIAL_DANIFICADO", label: "Material danificado" },
  { value: "KIT_INCOMPLETO", label: "Kit incompleto / faltou" },
  { value: "ERRO_SEPARACAO", label: "Erro de separação" },
  { value: "PERDA_EXTRAVIO", label: "Perda / extravio" },
  { value: "QUEBRA_PRODUCAO", label: "Quebra na produção" },
  { value: "QUEBRA_MONTAGEM", label: "Quebra na montagem" },
  { value: "DEFEITO_FORNECEDOR", label: "Defeito do fornecedor" },
  { value: "COMPLEMENTO_PEDIDO", label: "Complemento de pedido" },
  { value: "ALTERACAO_PROJETO", label: "Alteração de projeto" },
  { value: "CONSUMO_GERAL", label: "Consumo geral / ferramenta" },
  { value: "OUTRO", label: "Outro" },
];

export const MOTIVO_REQUISICAO_LABEL = Object.fromEntries(
  MOTIVOS_REQUISICAO.map((m) => [m.value, m.label]),
);

export const SETORES_ASP = [
  { value: "CORTE", label: "Corte" },
  { value: "COLADEIRA", label: "Coladeira" },
  { value: "FURACAO", label: "Furação" },
  { value: "MONTAGEM", label: "Montagem" },
  { value: "ACABAMENTO", label: "Acabamento" },
  { value: "EMBALAGEM", label: "Embalagem" },
];

export const UNIDADES_MEDIDA_RQI = [
  "UN", "PC", "CX", "KG", "M", "M2", "ML", "PAR", "RL", "CH",
];

// ---------- Prazo por rota / classificação (espelha o backend) ----------

export const ROTAS_PRAZO = [
  { value: "CORTE", label: "Corte / bordo / furação" },
  { value: "USINAGEM_PINTURA", label: "Usinagem / pintura" },
  { value: "TERCEIRO", label: "Terceiro (vidro, serralheria, tapeçaria)" },
  { value: "MAO_DE_OBRA", label: "Só mão de obra" },
  { value: "ALMOXARIFADO", label: "Almoxarifado (RQA/RQI)" },
];

// Cor só comunica estado (diretriz PharuERP).
export const ESTADOS_PRAZO = [
  { value: "SEM_PRAZO", label: "Sem prazo", tone: "neutral" },
  { value: "NO_PRAZO", label: "No prazo", tone: "info" },
  { value: "VENCE_HOJE", label: "Vence hoje", tone: "warning" },
  { value: "VENCIDO", label: "Vencido", tone: "danger" },
  { value: "CONCLUIDO_NO_PRAZO", label: "Concluído no prazo", tone: "success" },
  { value: "CONCLUIDO_COM_ATRASO", label: "Concluído com atraso", tone: "danger" },
];
export const ESTADO_PRAZO_LABEL = Object.fromEntries(ESTADOS_PRAZO.map((e) => [e.value, e.label]));
export const ESTADO_PRAZO_TONE = Object.fromEntries(ESTADOS_PRAZO.map((e) => [e.value, e.tone]));

export const MOTIVOS_REPLANEJAMENTO = [
  { value: "FALTA_MATERIAL", label: "Falta de material" },
  { value: "AGUARDANDO_TERCEIRO", label: "Aguardando terceiro" },
  { value: "PEDIDO_DO_CLIENTE", label: "Pedido do cliente" },
  { value: "CAPACIDADE", label: "Capacidade da fábrica" },
  { value: "CORRECAO_DE_REGRA", label: "Correção da regra/rota" },
  { value: "OUTRO", label: "Outro" },
];

export const IMPUTACOES = [
  { value: "INTERNA", label: "Interna (custo da não-qualidade)" },
  { value: "CLIENTE", label: "Cliente" },
  { value: "FORNECEDOR", label: "Fornecedor" },
  { value: "PRESTADOR", label: "Prestador / empreiteira" },
  { value: "GARANTIA", label: "Garantia" },
  { value: "A_DEFINIR", label: "A definir" },
  { value: "FORA_DO_CUSTO", label: "Fora do custo" },
];
export const IMPUTACAO_LABEL = Object.fromEntries(IMPUTACOES.map((i) => [i.value, i.label]));

// Códigos por tipo (`solicitacao_{sigla}:acao`, solicitacoes/acesso.py) para
// as ações pedidas — quem só tem o código do tipo também entra na página.
function codigosPorTipo(acoes) {
  return TIPOS_SOLICITACAO.flatMap((t) =>
    acoes.map((acao) => `solicitacao_${t.value.toLowerCase()}:${acao}`),
  );
}

// Página do colaborador (menu + rota leem daqui para não se desencontrarem).
export const PERMS_HUB_SOLICITACOES = [
  "solicitacao:view",
  "solicitacao_rqc:view",
  "solicitacao_rqc:create",
  ...codigosPorTipo(["view", "create"]),
];

// Permissões que caracterizam o gestor (qualquer uma libera).
export const PERMS_GESTAO_SOLICITACOES = [
  "solicitacao:autorizar",
  "solicitacao:atender",
  "solicitacao:gerar_lote",
  ...codigosPorTipo(["autorizar", "atender"]),
];

// Página de gestão: gestor ou quem só configura os catálogos.
export const PERMS_ROTA_GESTAO_SOLICITACOES = [
  ...PERMS_GESTAO_SOLICITACOES,
  "solicitacao:configurar",
];

// ---------- Acesso granular por TIPO (RBAC tipo × ação) ----------
// Espelha solicitacoes/acesso.py: tem a ação para o tipo se possui o código
// GLOBAL `solicitacao:acao` OU o do tipo `solicitacao_{sigla}:acao`. `has` é o
// hasPermission do useAuth (já faz bypass de Master).

export function podeAcaoTipo(has, acao, tipo) {
  if (!tipo) return has(`solicitacao:${acao}`);
  return (
    has(`solicitacao:${acao}`) ||
    has(`solicitacao_${String(tipo).toLowerCase()}:${acao}`)
  );
}

/** Siglas (de uma lista) em que o usuário tem a ação. */
export function tiposComAcao(has, acao, tipos) {
  return (tipos || []).filter((t) => podeAcaoTipo(has, acao, t));
}

// ---------------------------------------------------------------- linha do tempo

/** Feed de auditoria da Gestão: { eventos: [...], tipos: [{value,label,tone}] }. */
export function listarEventosSolicitacoes(request, { desde, ate, tipos, limite } = {}) {
  const qs = new URLSearchParams();
  if (desde) qs.set("desde", desde);
  if (ate) qs.set("ate", ate);
  if (tipos?.length) qs.set("tipos", tipos.join(","));
  if (limite) qs.set("limite", String(limite));
  const sufixo = qs.toString();
  return request(`/api/solicitacoes/eventos${sufixo ? `?${sufixo}` : ""}`, { silentError: true });
}

// ---------------------------------------------------------------- 22/09/2026

/** Itens do Cadastro de Suprimentos para o item de requisição (busca ≥ 2 letras). */
export function buscarProdutosSolicitacao(request, busca) {
  return request(`/api/solicitacoes/produtos-select?busca=${encodeURIComponent(busca)}`, { silentError: true });
}

/** Dias úteis e feriados que as contas de prazo usam: { dias_uteis, feriados: [{data, nome}] }. */
export function obterCalendarioSolicitacoes(request, de, ate) {
  return request(`/api/solicitacoes/calendario?de=${de}&ate=${ate}`, { silentError: true });
}

/** Quantas autorizadas estão sem prazo: { pendentes }. */
export function contarPrazosRetroativos(request) {
  return request("/api/solicitacoes/prazos/retroativos", { silentError: true });
}

/** Ação única: dá prazo às autorizadas antes da regra existir (pela data da autorização). */
export function calcularPrazosRetroativos(request) {
  return request("/api/solicitacoes/prazos/retroativos", { method: "POST" });
}
